import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  completeGoogleWorkspaceOAuth,
  formatGoogleWorkspaceError,
  GOOGLE_WORKSPACE_MESSAGE_TYPE,
} from './services/googleWorkspaceService';

const FALLBACK_RETURN_TO = '/curriculum';

type CallbackState = {
  pending: boolean;
  success: boolean;
  message: string;
  returnTo: string;
};

export default function GoogleWorkspaceOAuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [state, setState] = useState<CallbackState>({
    pending: true,
    success: false,
    message: 'Finishing Google Workspace connection...',
    returnTo: FALLBACK_RETURN_TO,
  });

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    void (async () => {
      const stateToken = searchParams.get('state')?.trim() ?? '';
      const code = searchParams.get('code')?.trim() ?? '';
      const errorCode = searchParams.get('error')?.trim() ?? '';
      const errorDescription = searchParams.get('error_description')?.trim() ?? '';

      if (!stateToken) {
        setState({
          pending: false,
          success: false,
          message: 'Missing Google OAuth state. Start the connection flow again.',
          returnTo: FALLBACK_RETURN_TO,
        });
        return;
      }

      try {
        const result = await completeGoogleWorkspaceOAuth({
          state: stateToken,
          code: code || undefined,
          error: errorCode || undefined,
          errorDescription: errorDescription || undefined,
        });

        if (result.success) {
          notifyOpener(result);
          setState({
            pending: false,
            success: true,
            message: result.message,
            returnTo: result.returnTo || FALLBACK_RETURN_TO,
          });
          if (window.opener && !window.opener.closed) {
            window.close();
            return;
          }
          navigate(result.returnTo || FALLBACK_RETURN_TO, { replace: true });
          return;
        }

        notifyOpener(result);
        if (window.opener && !window.opener.closed) {
          window.close();
          return;
        }
        setState({
          pending: false,
          success: false,
          message: result.message,
          returnTo: result.returnTo || FALLBACK_RETURN_TO,
        });
      } catch (error) {
        const message = formatGoogleWorkspaceError(
          error,
          'Google authorization was not completed.'
        );
        notifyOpener({
          success: false,
          code: errorCode || 'GOOGLE_AUTH_FAILED',
          message,
          returnTo: FALLBACK_RETURN_TO,
        });
        if (window.opener && !window.opener.closed) {
          window.close();
          return;
        }
        setState({
          pending: false,
          success: false,
          message,
          returnTo: FALLBACK_RETURN_TO,
        });
      }
    })();
  }, [navigate, searchParams]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 3,
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 420, width: '100%' }}>
        <Typography variant="h5">Google Workspace</Typography>
        {state.pending ? (
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CircularProgress size={22} />
            <Typography>{state.message}</Typography>
          </Stack>
        ) : (
          <>
            <Alert severity={state.success ? 'success' : 'error'}>{state.message}</Alert>
            {!state.success ? (
              <Button
                variant="contained"
                onClick={() => navigate(state.returnTo, { replace: true })}
              >
                Return to app
              </Button>
            ) : null}
          </>
        )}
      </Stack>
    </Box>
  );
}

function notifyOpener(result: {
  success: boolean;
  code: string;
  message: string;
  returnTo: string;
}) {
  try {
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage(
        {
          type: GOOGLE_WORKSPACE_MESSAGE_TYPE,
          ...result,
        },
        window.location.origin
      );
    }
  } catch {
    // no-op
  }
}
